import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { format, isToday } from "date-fns";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "@awesome.me/webawesome/dist/components/button/button.js";
import { selectedDateService } from "../services/selected-date";

/**
 * Header-injected date stepper.
 *
 * Page-sensitive control: only pages that operate on a "selected day"
 * (Today, Dashboard) slot this into `app-shell`'s `header-center` slot.
 * History/Settings/etc. show their page title instead.
 *
 * Reads/writes the shared selectedDateService so other listeners stay
 * in sync.
 */
@customElement("header-date-stepper")
export class HeaderDateStepper extends LitElement {
  @state() private selectedDate: Date = selectedDateService.date;
  @state() private datePickerOpen = false;

  private _unsubscribe?: () => void;

  static styles = css`
    :host {
      display: block;
    }

    .stepper {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;
    }

    button {
      background: transparent;
      border: none;
      color: var(--color-text, #1f2d3d);
      padding: 0.5rem;
      border-radius: 0.5rem;
      cursor: pointer;
      display: flex;
      align-items: center;
    }

    button:hover {
      background: rgba(31, 45, 61, 0.08);
    }

    button wa-icon {
      font-size: 1.1rem;
    }

    .label {
      font-size: 0.95rem;
      font-weight: 600;
      min-width: 8rem;
      text-align: center;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 0.375rem;
      color: var(--color-text, #1f2d3d);
    }

    .label:hover {
      background: rgba(31, 45, 61, 0.08);
    }

    .picker-body {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding: 0.5rem 0;
    }

    .picker-body input[type="date"] {
      font-size: 1rem;
      padding: 0.5rem;
      border-radius: 0.5rem;
      border: 1px solid var(--color-border, #dce6f4);
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribe = selectedDateService.subscribe((date) => {
      this.selectedDate = date;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
  }

  private _prev = () => selectedDateService.shiftDays(-1);
  private _next = () => selectedDateService.shiftDays(1);
  private _open = () => {
    this.datePickerOpen = true;
  };
  private _close = () => {
    this.datePickerOpen = false;
  };
  private _pick = (e: Event) => {
    const value = (e.target as HTMLInputElement).value;
    if (!value) return;
    const [y, m, d] = value.split("-").map(Number);
    selectedDateService.set(new Date(y, m - 1, d));
    this.datePickerOpen = false;
  };
  private _today = () => {
    selectedDateService.goToToday();
    this.datePickerOpen = false;
  };

  private _formatLabel(): string {
    if (isToday(this.selectedDate)) return "Today";
    return format(this.selectedDate, "EEE, d MMM");
  }

  render() {
    return html`
      <div class="stepper">
        <button @click=${this._prev} aria-label="Previous day">
          <wa-icon name="chevron-left"></wa-icon>
        </button>
        <span class="label" @click=${this._open} title="Pick a date">
          ${this._formatLabel()}
        </span>
        <button @click=${this._next} aria-label="Next day">
          <wa-icon name="chevron-right"></wa-icon>
        </button>
      </div>

      <wa-dialog
        ?open=${this.datePickerOpen}
        label="Pick a date"
        @wa-after-hide=${this._close}
      >
        <div class="picker-body">
          <input
            type="date"
            .value=${format(this.selectedDate, "yyyy-MM-dd")}
            @change=${this._pick}
          />
          <wa-button @click=${this._today}>Go to today</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "header-date-stepper": HeaderDateStepper;
  }
}
